import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useMemo } from "react";
import { SB3RuleContext } from "../../Components/Rules/SB3RuleContext";
import Table from "../../Components/Rules/RulesTable";
import useTable from "Components/Common/Table/useTable";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import RulesDialog from "./RulesDialog";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import Button from "Components/DesignSystem/Button/Button";
import { CREATE, EMPTY_RULES, LOGIC } from "../../ISRConstants";
import { deleteRule, updateRule } from "../../service";

type RulesComponentProps = {
  projectId: string | null | undefined
  id: string;
  onRuleDeleted?: () => void;
};

const RulesComponent = (props: React.PropsWithChildren<RulesComponentProps>) => {
  const { onRuleDeleted } = props;
  const { getRules, showRulesMenu, toggleMenu, } = useContext(SB3RuleContext)
  const [selectedRuleType, setSelectedRuleType] = useState<string>(LOGIC);

  const handleUpdateRule = async (e: any, data: any) => {
    try {
      const body: any = { ...data }
      body.is_disabled = !body.is_disabled
      const res = await updateRule(body)
      if (res?.data?.done) {
        toast.success("Rule updated successfully")
        fetchTableData(true)
      }
    } catch (error) {
      toast.error("Rule update unsuccessful")
    }
  }

  const RULES_HEADERS = [
    { label: "Name", map: "name", options: { copy: true, isLink: true, sort: true } },
    { label: "Rule Type", map: "rule_type", options: { sort: true } },
    { label: "Description", map: "description", options: { sort: false } },
    {
      label: "Enable/Disable", map: "is_disabled", options: {
        sort: true, type: "switch", handleSwitch: handleUpdateRule
      }
    },
    { label: "DELETE", map: "delete", options: { sort: false, index: true } },
  ];

  const [recordPerPage, setRecordPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const shouldScrollToBottomRef = useRef(false);
  const hasLoadedOnce = useRef(false);
  const [loading, setLoading] = useState(true);
  const { headers, data, sort, setData, handleSortByColumn } =
    useTable({ table_headers: RULES_HEADERS });

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;
    return data.filter((row: any) =>
      String(row?.name ?? "").toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };


  const fetchTableData = async (silent?: boolean): Promise<any[]> => {
    try {
      if (!silent) setLoading(true);
      if (props?.projectId) {
        const res = await getRules(props?.projectId);
        if (res?.data?.data) {
          setData(res?.data?.data);
          return res?.data?.data;
        }
      }
      return [];
    } catch (error) {
      if (!silent) setLoading(false);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleRecordPerPageChange = (perPage: number) => {
    setRecordPerPage(perPage);
    setPage(0);
  };
  const handleEdit = (index: number) => {
    const cat: any = filteredData[index];
    setSelectedRuleType(cat?.rule_type === 'display' ? LOGIC : LOGIC);
    toggleMenu(cat?.id);
  };

  const handleDelete = async (index: number) => {
    const cat: any = filteredData[index];
    const res = await deleteRule(cat?.id);
    if (res) {
      toast.success("Deleted successfully");
      onRuleDeleted?.();
    } else {
      toast.warning("Delete Unsuccessful");
    }
    const newData = await fetchTableData(true);
    if (newData.length > 0) {
      const totalPages = Math.ceil(newData.length / recordPerPage);
      if (page > totalPages) {
        shouldScrollToBottomRef.current = true;
        setPage(totalPages);
      }
    }
  };

  useEffect(() => {
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      fetchTableData();
    } else {
      fetchTableData(true);
    }
  }, [showRulesMenu]);

  useLayoutEffect(() => {
    if (!shouldScrollToBottomRef.current) return;
    shouldScrollToBottomRef.current = false;
    requestAnimationFrame(() => {
      /* Anchored on the DS Table's testid wrapper. This used to read
         `#rules-table-body tr:last-child` — an id that only existed on the
         hand-rolled <tbody> and silently stopped matching the moment the grid
         became a DS Table, taking the scroll-into-view with it. */
      const lastRow = document.querySelector('[data-testid="relationships-table"] tbody tr:last-child');
      if (lastRow) lastRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [page]);

  return (
    <div id={props.id} className="tw-w-[100%] tw-mt-2">
      <Table
        headers={headers}
        recordPerPage={recordPerPage}
        setRecordPerPage={handleRecordPerPageChange}
        handlePageChange={setPage}
        data={filteredData}
        totalCount={data?.length ?? 0}
        loading={loading}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        handleSortByColumn={handleSortByColumn}
        sort={sort}
        page={page}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        NoDataComponent={() => (
          <EmptyState
            id="create-rules"
            title={EMPTY_RULES}
            illustration={<NoDataIllustration />}
            action={
              <Button
                variant="primary"
                onClick={() => { toggleMenu() }}
                data-testid="relationships-empty-create-btn"
              >
                {CREATE}
              </Button>
            }
            data-testid="relationships-empty-state"
          />
        )}
        createMethod={() => { toggleMenu() }}
      />
      {
        showRulesMenu && <RulesDialog projectId={props.projectId} />
      }
    </div>
  );
};

export default RulesComponent;
